/**
 * Relay store
 */

import isDeepEqual from 'fast-deep-equal';
import type { KeyType, KeyTypeData } from 'react-relay/relay-hooks/helpers';
import type { usePaginationFragmentHookType } from 'react-relay/relay-hooks/usePaginationFragment';
import type { OperationType } from 'relay-runtime';
import type { DeepWritable, PickKeys } from 'ts-essentials';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  ContactPaginationFragment,
  ContactSelectPaginationFragment,
  ContactSelectQuery,
  type Contact_Pagination_Fragment$args,
  type Contact_Pagination_Fragment$data,
  type Contact_Pagination_Fragment$key,
  type Contact_Select_Pagination_Fragment$args,
  type Contact_Select_Pagination_Fragment$data,
  type Contact_Select_Pagination_Fragment$key,
  type Contact_Select_Query,
  ContactsRootQuery,
  type Contacts_Root_Query,
} from './queries/Contact.query';
import {
  type CrmList_Pagination_Fragment$args,
  CrmListsQuery,
  type CrmLists_Pagination_Query,
} from './queries/CrmList.query';
import {
  FormsPaginationFragment,
  FormsQuery,
  type Forms_Pagination_Fragment$args,
  type Forms_Pagination_Fragment$data,
  type Forms_Pagination_Fragment$key,
  type Forms_Query,
} from './queries/Form.query';
import {
  FormEntriesPaginationFragment,
  FormEntryRootQuery,
  type FormEntry_Pagination_Fragment$args,
  type FormEntry_Pagination_Fragment$data,
  type FormEntry_Pagination_Fragment$key,
  type FormEntry_Root_Query,
} from './queries/FormEntry.query';
import {
  HubsPaginationFragment,
  HubsQuery,
  type Hubs_Pagination_Fragment$args,
  type Hubs_Pagination_Fragment$data,
  type Hubs_Pagination_Fragment$key,
  type Hubs_Query,
} from './queries/Hub.query';
import {
  HubPagePaginationFragment,
  type HubPage_Query,
  HubPagesQuery,
  type HubPages_Pagination_Fragment$args,
  type HubPages_Pagination_Fragment$data,
  type HubPages_Pagination_Fragment$key,
} from './queries/HubPage.query';
import {
  MediaCatalogQuery,
  MediaCatalogQueryPaginationFragment,
  MediaQuery,
  MediaQueryPaginationFragment,
  type Media_Catalog_Query,
  type Media_Catalog_Query_Pagination_Fragment$args,
  type Media_Catalog_Query_Pagination_Fragment$data,
  type Media_Catalog_Query_Pagination_Fragment$key,
  type Media_Query,
  type Media_Query_Pagination_Fragment$args,
  type Media_Query_Pagination_Fragment$data,
  type Media_Query_Pagination_Fragment$key,
} from './queries/Media.query';
import {
  type Tag_Pagination_Fragment$args,
  type Tag_Pagination_Fragment$data,
  type Tag_Pagination_Fragment$key,
  TagsPaginationFragment,
  TagsQuery,
  type Tags_Query,
} from './queries/Tag.query';
import {
  UsersQuery,
  type Users_Pagination_Fragment$args,
  type Users_Pagination_Fragment$data,
  type Users_Pagination_Fragment$key,
  type Users_Query,
} from './queries/User.query';
import {
  ZnglistsPaginationFragment,
  ZnglistsQuery,
  type Znglists_Pagination_Fragment$args,
  type Znglists_Pagination_Fragment$data,
  type Znglists_Pagination_Fragment$key,
  type Znglists_Query,
} from './queries/Znglist.query';
import { BackendQuery, type ZnglyContext_Backend_Query } from './queries/ZnglyContext.query';
import {
  ZnglyVideosPaginationFragment,
  type ZnglyVideos_Pagination_Fragment$args,
  type ZnglyVideos_Pagination_Fragment$data,
  type ZnglyVideos_Pagination_Fragment$key,
} from './queries/ZnglyVideo.query';
import type {
  CrmList_Pagination_Fragment$data,
  CrmList_Pagination_Fragment$key,
} from './queries/__generated__/CrmList_Pagination_Fragment.graphql';

type PaginationData<TQuery extends OperationType, TKey extends KeyType> = Omit<
  usePaginationFragmentHookType<TQuery, TKey, KeyTypeData<TKey>>,
  'loadNext' | 'loadPrevious' | 'refetch'
>;

const initPaginationData = Object.freeze({
  data: null,
  hasNext: false,
  hasPrevious: false,
  isLoadingNext: false,
  isLoadingPrevious: false,
});

const initRelayStore = Object.freeze({
  ZnglyVideos_Pagination_Fragment: {
    __typename: 'RootQueryToZnglyVideoConnection',
    __id: [] as string[],
    getOperationQuery: () => BackendQuery,
    __operationType: undefined as unknown as ZnglyContext_Backend_Query,
    getPaginationQuery: () => ZnglyVideosPaginationFragment,
    __paginationVariables: undefined as unknown as ZnglyVideos_Pagination_Fragment$args,
    __paginationQueryDataType: undefined as unknown as ZnglyVideos_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as ZnglyVideos_Pagination_Fragment$key,
  },
  Category_Fragment: {
    __typename: 'RootQueryToCategoryConnection',
    __id: [] as string[],
    __paginationVariables: {},
  },
  Tags_Pagination_Fragment: {
    __typename: 'RootQueryToTagConnection',
    __id: [] as string[],
    getOperationQuery: () => TagsQuery,
    __operationType: undefined as unknown as Tags_Query,
    getPaginationQuery: () => TagsPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Tag_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Tag_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Tag_Pagination_Fragment$args,
  },
  Forms_Pagination_Fragment: {
    __typename: 'RootQueryToFormConnection',
    __id: [] as string[],
    getOperationQuery: () => FormsQuery,
    __operationType: undefined as unknown as Forms_Query,
    getPaginationQuery: () => FormsPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Forms_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Forms_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Forms_Pagination_Fragment$args,
  },
  FormEntry_Pagination_Fragment: {
    __typename: 'RootQueryToFormEntryConnection',
    __id: [] as string[],
    getOperationQuery: () => FormEntryRootQuery,
    __operationType: undefined as unknown as FormEntry_Root_Query,
    getPaginationQuery: () => FormEntriesPaginationFragment,
    __paginationQueryDataType: undefined as unknown as FormEntry_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as FormEntry_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as FormEntry_Pagination_Fragment$args,
  },
  Hubs_Pagination_Fragment: {
    __typename: 'RootQueryToHubConnection',
    __id: [] as string[],
    getOperationQuery: () => HubsQuery,
    __operationType: undefined as unknown as Hubs_Query,
    getPaginationQuery: () => HubsPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Hubs_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Hubs_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Hubs_Pagination_Fragment$args,
    paginationData: { ...initPaginationData } as unknown as PaginationData<Hubs_Query, Hubs_Pagination_Fragment$key>,
  },
  HubPages_Pagination_Fragment: {
    __typename: 'RootQueryToHubPageConnection',
    __id: [] as string[],
    getOperationQuery: () => HubPagesQuery,
    __operationType: undefined as unknown as HubPage_Query,
    getPaginationQuery: () => HubPagePaginationFragment,
    __paginationQueryDataType: undefined as unknown as HubPages_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as HubPages_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as HubPages_Pagination_Fragment$args,
  },
  Media_Query_Pagination_Fragment: {
    __typename: 'RootQueryToMediaItemConnection',
    __id: [] as string[],
    getOperationQuery: () => MediaQuery,
    __operationType: undefined as unknown as Media_Query,
    getPaginationQuery: () => MediaQueryPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Media_Query_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Media_Query_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Media_Query_Pagination_Fragment$args,
  },
  Media_Catalog_Query_Pagination_Fragment: {
    __typename: 'RootQueryToMediaItemConnection',
    __id: [] as string[],
    getOperationQuery: () => MediaCatalogQuery,
    __operationType: undefined as unknown as Media_Catalog_Query,
    getPaginationQuery: () => MediaCatalogQueryPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Media_Catalog_Query_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Media_Catalog_Query_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Media_Catalog_Query_Pagination_Fragment$args,
  },
  Users_Management_Fragment: {
    __typename: 'RootQueryToUserConnection',
    __id: [] as string[],
    __paginationVariables: {},
  },
  Users_Fragment: {
    __typename: 'RootQueryToUserConnection',
    __id: [] as string[],
    __paginationVariables: {},
  },
  Users_Pagination_Fragment: {
    __typename: 'RootQueryToUserConnection',
    __id: [] as string[],
    getOperationQuery: () => UsersQuery,
    __operationType: undefined as unknown as Users_Query,
    getPaginationQuery: () => MediaQueryPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Users_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Users_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Users_Pagination_Fragment$args,
  },
  Znglists_Pagination_Fragment: {
    __typename: 'RootQueryToZnglistConnection',
    __id: [] as string[],
    getOperationQuery: () => ZnglistsQuery,
    __operationType: undefined as unknown as Znglists_Query,
    getPaginationQuery: () => ZnglistsPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Znglists_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Znglists_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Znglists_Pagination_Fragment$args,
  },
  Contact_Select_Pagination_Fragment: {
    __typename: 'RootQueryToContactConnection',
    __id: [] as string[],
    getOperationQuery: () => ContactSelectQuery,
    __operationType: undefined as unknown as Contact_Select_Query,
    getPaginationQuery: () => ContactSelectPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Contact_Select_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Contact_Select_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Contact_Select_Pagination_Fragment$args,
  },
  Contact_Pagination_Fragment: {
    __typename: 'RootQueryToContactConnection',
    __id: [] as string[],
    getOperationQuery: () => ContactsRootQuery,
    __operationType: undefined as unknown as Contacts_Root_Query,
    getPaginationQuery: () => ContactPaginationFragment,
    __paginationQueryDataType: undefined as unknown as Contact_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as Contact_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as Contact_Pagination_Fragment$args,
  },
  CrmList_Pagination_Fragment: {
    __typename: 'RootQueryToCrmListConnection',
    __id: [] as string[],
    getOperationQuery: () => CrmListsQuery,
    __operationType: undefined as unknown as CrmLists_Pagination_Query,
    getPaginationQuery: () => ContactPaginationFragment,
    __paginationQueryDataType: undefined as unknown as CrmList_Pagination_Fragment$data,
    __paginationQueryKeyType: undefined as unknown as CrmList_Pagination_Fragment$key,
    __paginationVariables: undefined as unknown as CrmList_Pagination_Fragment$args,
  },
});

type InitRelayStore = DeepWritable<typeof initRelayStore>;

type RelayStoreWithPaginationDataKeys = PickKeys<InitRelayStore, { paginationData: unknown }>;
type StoreKey = keyof InitRelayStore;

type Actions = {
  get__id: (key: StoreKey) => string[];
  set__id: (key: StoreKey, value: string[] | string) => void;
  getByKey: <T extends StoreKey>(key: T) => RelayStore[T];
  getPaginationData: <T extends RelayStoreWithPaginationDataKeys>(key: T) => InitRelayStore[T]['paginationData'];
  setPaginationData: <T extends RelayStoreWithPaginationDataKeys>(
    key: T,
    value: InitRelayStore[T]['paginationData']
  ) => void;
};

type RelayStore = InitRelayStore & Actions;

export type { StoreKey as RelayStoreKey, RelayStore };

export const useRelayStore = createWithEqualityFn<RelayStore>(
  (set, get) => ({
    ...(initRelayStore as InitRelayStore),
    get__id: (key) => get()?.[key]?.__id ?? [],
    set__id: (key, _values) => {
      let values = _values;
      if (!values) return;
      if (typeof values === 'string') values = [values];
      if (values.length === 0) return;
      if (!get()?.[key]) return;

      values = values.filter((value) => !!value);

      set((state) => {
        const newState = { ...state };
        const oldValues = state?.[key]?.__id ?? [];

        // we can only have unique values
        newState[key].__id = Array.from(new Set([...oldValues, ...values]));
        return newState;
      });
    },
    getByKey: <T extends StoreKey>(key: T) => get()?.[key],
    getPaginationData: (key) => get()?.[key]?.paginationData ?? null,
    setPaginationData: (key, value) => {
      set((state) => {
        if (!state?.[key] || !Object.hasOwn(state?.[key], 'paginationData')) return state;

        const newState = { ...state };
        newState[key].paginationData = {
          data: value.data,
          hasNext: value.hasNext,
          hasPrevious: value.hasPrevious,
          isLoadingNext: value.isLoadingNext,
          isLoadingPrevious: value.isLoadingPrevious,
        };
        return newState;
      });
    },
  }),
  (pastState, currentState) => isDeepEqual(pastState, currentState)
);
